import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import type { IRecordLogDTO } from '@shared/common';

/** File-local constants for LogClient. */
const localConstants = {
  routePost: 'log',
  routeGatedList: 'log/gated',
  routeGatedCount: 'log/gated/count',
  routeGatedDetail: (logID: string): string => `log/gated/${logID}`,
  routeGatedClear: 'log/gated/clear',
  contentType: 'application/json',
  errPostFailed: 'Failed to post log batch',
  errListFailed: 'Failed to fetch logs',
  errCountFailed: 'Failed to fetch log count',
  errGetFailed: 'Failed to fetch log details',
  errClearFailed: 'Failed to clear logs'
} as const;

/**
 * Client for the public log recording endpoint exposed by the lobby service. Used by {@link RecordLoggerFrontend} — not intended for direct call sites.
 *
 * Singleton, public (no auth adapter), best-effort. Errors propagate so the caller (frontend recorder) can decide whether to retry / drop.
 */
export class LogClient {
  /** Singleton instance. */
  private static instance: LogClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton LogClient instance. */
  public static GetInstance(): LogClient {
    return LogClient.instance ?? (LogClient.instance = new LogClient());
  }

  /**
   * POST /api/lobby/log - Sends a batch of log entries to the lobby service.
   *
   * @param entries - Batch of warn/error entries. Server-managed fields (`hash`, `count`, `createdAt`, `modifiedAt`) are ignored if supplied.
   * @param signal - AbortSignal to cancel the request (e.g. on `beforeunload`).
   * @returns `'OK'` on successful persistence.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async PostBatch(entries: IRecordLogDTO[], signal?: AbortSignal): Promise<string> {
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, localConstants.routePost, {
      method: 'POST',
      headers: { 'Content-Type': localConstants.contentType },
      body: JSON.stringify(entries),
      signal
    });
    if (!res.ok) throw new Error(localConstants.errPostFailed);
    return await res.text();
  }

  /**
   * GET /api/lobby/log/gated - Lists recorded log entries newest-first (privileged).
   *
   * @param signal - AbortSignal to cancel the request.
   * @param limit - Max entries to return.
   * @param offset - Number of entries to skip.
   * @returns Recorded log documents.
   */
  public async GetLogs(signal: AbortSignal, limit: number = 100, offset: number = 0): Promise<IRecordLogDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `${localConstants.routeGatedList}?${params}`, { signal }, auth);
    if (!res.ok) throw new Error(localConstants.errListFailed);
    return await res.json();
  }

  /**
   * GET /api/lobby/log/gated/count - Total number of stored log entries (privileged).
   *
   * @param signal - AbortSignal to cancel the request.
   * @returns The total count.
   */
  public async GetLogsCount(signal: AbortSignal): Promise<number> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, localConstants.routeGatedCount, { signal }, auth);
    if (!res.ok) throw new Error(localConstants.errCountFailed);
    return await res.json();
  }

  /**
   * GET /api/lobby/log/gated/:logID - Single log document (privileged).
   *
   * @param signal - AbortSignal to cancel the request.
   * @param logID - The log document id.
   * @returns The log document, or `null` when not found.
   */
  public async GetLogByID(signal: AbortSignal, logID: string): Promise<IRecordLogDTO | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, localConstants.routeGatedDetail(logID), { signal }, auth);
    if (!res.ok) throw new Error(localConstants.errGetFailed);
    return await res.json();
  }

  /**
   * DELETE /api/lobby/log/gated/clear - Wipes the entire log collection (privileged).
   *
   * @param signal - AbortSignal to cancel the request.
   * @returns `'CLEARED'` on success.
   */
  public async ClearLogs(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, localConstants.routeGatedClear, { method: 'DELETE', signal }, auth);
    if (!res.ok) throw new Error(localConstants.errClearFailed);
    return await res.text();
  }
}
